import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment-timezone';
import AreaShell from '../../chrome/AreaShell';
import useLicenseTypeManagement from '../../../Licenses/components/hooks/useLicenseTypeManagement';
import useFeatureFlagManagement from '../../../Features/components/hooks/useFeatureFlagManagement';
import useOrgs from '../../../Orgs/components/hooks/useOrgs';
import { eventDay } from '../../../v2/lib/eventStats';
import { Checkbox, ConfirmDialog, Dialog, Field, SectionCard, Select, Textarea, TextInput } from '../../components/forms';
import { useList, useToast } from '../../components/roster';
import './admin-orgs.css';

/*
 * License types (device limit + feature grants) and the licenses issued
 * to orgs. Anything that changes what an org is entitled to asks first.
 * Licenses load per org (one request), not classic's N+1 loop.
 */

interface LicenseType {
    id: number;
    name: string;
    adminName: string;
    description: string | null;
    simultaneousDeviceCount: number;
}

interface License {
    id: number;
    key: string;
    licenseTypeId: number;
    licenseTypeName?: string;
    orgName?: string;
    expiresAt: string | null;
    expiredAt: string | null;
    createdAt: string;
}

interface Org {
    id: number;
    name: string;
}

interface Flag {
    id: number;
    key: string;
}

type TypeDraft = { name: string; adminName: string; description: string; simultaneousDeviceCount: string };

const EMPTY_TYPE: TypeDraft = { name: '', adminName: '', description: '', simultaneousDeviceCount: '5' };

const isExpired = (l: License) =>
    !!l.expiredAt || (!!l.expiresAt && eventDay(l.expiresAt).endOf('day').isBefore(moment()));
const day = (d: string) => eventDay(d).format('MMM D, YYYY');
const devices = (n: number) => `${n} device${n === 1 ? '' : 's'}`;
const keyTail = (k: string) => (k && k.length > 8 ? `…${k.slice(-8)}` : k);
const list = (xs: string[]) =>
    xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;

export default function AdminLicensesV3() {
    const {
        fetchLicenseTypes,
        createLicenseType,
        updateLicenseType,
        deleteLicenseType,
        listFeaturesForLicenseType,
        addFeatureToLicenseType,
        removeFeatureFromLicenseType,
        getLicensesForOrg,
        createLicense,
        expireLicense,
    } = useLicenseTypeManagement();
    const { fetchFeatureFlags } = useFeatureFlagManagement();
    const { listOrgs } = useOrgs();
    const toast = useToast();

    const types = useList<LicenseType>(() => fetchLicenseTypes());
    const orgs = useList<Org>(() =>
        listOrgs().then((rows: Org[]) => [...(rows ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))),
    );
    const typeRows = useMemo(() => types.rows ?? [], [types.rows]);
    const orgRows = useMemo(() => orgs.rows ?? [], [orgs.rows]);
    const typeName = (id: number) => typeRows.find((t) => t.id === id)?.name;
    const orgName = (id: number | string) => orgRows.find((o) => String(o.id) === String(id))?.name ?? `org #${id}`;

    /* ?org= so Admin › Organizations can link straight to one org's licenses */
    const [params, setParams] = useSearchParams();
    const orgFilter = params.get('org') ?? '';
    const pickOrg = (id: string) => {
        const next = new URLSearchParams(params);
        if (id) next.set('org', id);
        else next.delete('org');
        setParams(next, { replace: true });
    };

    const [licenses, setLicenses] = useState<License[] | null>(null);
    const [licensesFailed, setLicensesFailed] = useState(false);
    const [licensesTick, setLicensesTick] = useState(0);
    const reloadLicenses = () => setLicensesTick((n) => n + 1);
    useEffect(() => {
        if (!orgFilter) {
            setLicenses(null);
            return;
        }
        let live = true;
        setLicenses(null);
        setLicensesFailed(false);
        getLicensesForOrg(+orgFilter)
            .then((rows) => live && setLicenses(rows ?? []))
            .catch(() => live && setLicensesFailed(true));
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgFilter, licensesTick]);
    const sortedLicenses = useMemo(
        () => [...(licenses ?? [])].sort((a, b) => Number(isExpired(a)) - Number(isExpired(b))),
        [licenses],
    );

    /* ---- license types: create / edit ---- */
    const [typeDialog, setTypeDialog] = useState<{ row: LicenseType | null } | null>(null);
    const [typeDraft, setTypeDraft] = useState<TypeDraft>(EMPTY_TYPE);
    const [typeTouched, setTypeTouched] = useState(false);
    const [typeError, setTypeError] = useState('');
    const [typeSaving, setTypeSaving] = useState(false);
    const [typeConfirm, setTypeConfirm] = useState(false);
    const typeInFlight = useRef(false);

    const openType = (row: LicenseType | null) => {
        setTypeDraft(
            row
                ? {
                      name: row.name ?? '',
                      adminName: row.adminName ?? '',
                      description: row.description ?? '',
                      simultaneousDeviceCount: String(row.simultaneousDeviceCount ?? 5),
                  }
                : EMPTY_TYPE,
        );
        setTypeTouched(false);
        setTypeError('');
        setTypeDialog({ row });
    };
    const count = Number(typeDraft.simultaneousDeviceCount);
    const typeInvalid = {
        name: !typeDraft.name.trim(),
        adminName: !typeDraft.adminName.trim(),
        count: !Number.isInteger(count) || count < 1,
    };
    const typeReady = !typeInvalid.name && !typeInvalid.adminName && !typeInvalid.count;
    const editingRow = typeDialog?.row ?? null;
    const countChanged = editingRow !== null && count !== editingRow.simultaneousDeviceCount;

    const saveType = async () => {
        if (!typeDialog || typeInFlight.current) return;
        typeInFlight.current = true;
        setTypeSaving(true);
        setTypeError('');
        const body = {
            name: typeDraft.name.trim(),
            adminName: typeDraft.adminName.trim(),
            description: typeDraft.description.trim(),
            simultaneousDeviceCount: count,
        };
        try {
            const row = typeDialog.row;
            const ok = row ? await updateLicenseType(row.id, body) : await createLicenseType(body);
            if (!ok) throw new Error('empty');
            setTypeDialog(null);
            toast.show(row ? `Saved ${body.name}.` : `Added the ${body.name} license type.`);
            types.reload();
        } catch {
            setTypeError('Couldn’t save — try again.');
        } finally {
            typeInFlight.current = false;
            setTypeSaving(false);
        }
    };
    const submitType = () => {
        setTypeTouched(true);
        if (!typeReady || typeSaving) return;
        /* a new device limit changes every org that holds this type */
        if (countChanged) setTypeConfirm(true);
        else saveType();
    };

    /* ---- license types: delete ---- */
    const [deleting, setDeleting] = useState<LicenseType | null>(null);
    const [busyTypeId, setBusyTypeId] = useState<number | null>(null);
    const removeType = async (row: LicenseType) => {
        if (busyTypeId !== null) return;
        setBusyTypeId(row.id);
        try {
            await deleteLicenseType(row.id);
            types.setRows((cur) => (cur ?? []).filter((t) => t.id !== row.id));
            toast.show(`Deleted the ${row.name} license type.`);
        } catch {
            toast.show(`Couldn’t delete ${row.name} — it may still be in use by a license.`);
        } finally {
            setBusyTypeId(null);
        }
    };

    /* ---- license types: feature grants (edited as a draft, applied after a confirm) ---- */
    const [featureType, setFeatureType] = useState<LicenseType | null>(null);
    const [flags, setFlags] = useState<Flag[] | null>(null);
    const [granted, setGranted] = useState<Set<number> | null>(null);
    const [grantDraft, setGrantDraft] = useState<Set<number>>(new Set());
    const [featuresFailed, setFeaturesFailed] = useState(false);
    const [featuresConfirm, setFeaturesConfirm] = useState(false);
    const [featuresSaving, setFeaturesSaving] = useState(false);
    const wantedType = useRef<number | null>(null);

    const openFeatures = (row: LicenseType) => {
        wantedType.current = row.id;
        setFeatureType(row);
        setGranted(null);
        setFeaturesFailed(false);
        Promise.all([
            flags ? Promise.resolve(flags) : fetchFeatureFlags(),
            listFeaturesForLicenseType(row.id),
        ])
            .then(([all, rows]) => {
                if (wantedType.current !== row.id) return;
                setFlags([...(all ?? [])].sort((a: Flag, b: Flag) => (a.key ?? '').localeCompare(b.key ?? '')));
                const ids = new Set<number>((rows ?? []).map((g) => g.featureId));
                setGranted(ids);
                setGrantDraft(new Set(ids));
            })
            .catch(() => wantedType.current === row.id && setFeaturesFailed(true));
    };
    const closeFeatures = () => {
        if (featuresSaving) return;
        wantedType.current = null;
        setFeatureType(null);
    };
    const flagKey = (id: number) => flags?.find((f) => f.id === id)?.key ?? `#${id}`;
    const adds = granted ? Array.from(grantDraft).filter((id) => !granted.has(id)) : [];
    const removes = granted ? Array.from(granted).filter((id) => !grantDraft.has(id)) : [];
    const applyFeatures = async () => {
        if (!featureType || featuresSaving) return;
        setFeaturesSaving(true);
        const results = await Promise.all([
            ...adds.map((id) => addFeatureToLicenseType(featureType.id, id).then(() => true, () => false)),
            ...removes.map((id) => removeFeatureFromLicenseType(featureType.id, id).then(() => true, () => false)),
        ]);
        setFeaturesSaving(false);
        const failedCount = results.filter((ok) => !ok).length;
        if (failedCount === 0) {
            toast.show(`Updated ${featureType.name}’s features.`);
            wantedType.current = null;
            setFeatureType(null);
        } else {
            toast.show(`${failedCount} of ${results.length} feature changes didn’t save — reopened with the current grants.`);
            openFeatures(featureType);
        }
    };

    /* ---- issue a license ---- */
    const [issueOpen, setIssueOpen] = useState(false);
    const [issueDraft, setIssueDraft] = useState({ orgId: '', licenseTypeId: '', expiresAt: '' });
    const [issueError, setIssueError] = useState('');
    const [issueConfirm, setIssueConfirm] = useState(false);
    const [issuing, setIssuing] = useState(false);
    const issueInFlight = useRef(false);
    const today = moment().format('YYYY-MM-DD');
    const pastExpiry = !!issueDraft.expiresAt && issueDraft.expiresAt < today;
    const issueReady = !!issueDraft.orgId && !!issueDraft.licenseTypeId && !pastExpiry;
    const issueType = typeRows.find((t) => String(t.id) === issueDraft.licenseTypeId);

    const openIssue = () => {
        setIssueDraft({ orgId: orgFilter, licenseTypeId: '', expiresAt: '' });
        setIssueError('');
        setIssueOpen(true);
    };
    const issue = async () => {
        if (!issueReady || issueInFlight.current) return;
        issueInFlight.current = true;
        setIssuing(true);
        setIssueError('');
        try {
            const ok = await createLicense({
                orgId: +issueDraft.orgId,
                licenseTypeId: +issueDraft.licenseTypeId,
                expiresAt: issueDraft.expiresAt || null,
            });
            if (!ok) throw new Error('empty');
            setIssueOpen(false);
            toast.show(`Issued a ${issueType?.name ?? ''} license to ${orgName(issueDraft.orgId)}.`);
            if (orgFilter === issueDraft.orgId) reloadLicenses();
            else pickOrg(issueDraft.orgId);
        } catch {
            setIssueError('Couldn’t issue the license — nothing was changed.');
        } finally {
            issueInFlight.current = false;
            setIssuing(false);
        }
    };

    /* ---- expire ---- */
    const [expiring, setExpiring] = useState<License | null>(null);
    const [busyLicenseId, setBusyLicenseId] = useState<number | null>(null);
    const expire = async (row: License) => {
        if (busyLicenseId !== null) return;
        setBusyLicenseId(row.id);
        try {
            await expireLicense(row.id);
            toast.show('License expired.');
            reloadLicenses();
        } catch {
            toast.show('Couldn’t expire the license — it’s unchanged.');
        } finally {
            setBusyLicenseId(null);
        }
    };
    const licenseLabel = (l: License) => l.licenseTypeName ?? typeName(l.licenseTypeId) ?? 'License';

    const orgPicker = (
        <Select
            aria-label="Organization"
            value={orgFilter}
            disabled={orgs.rows === null || orgs.failed}
            onChange={(e) => pickOrg(e.target.value)}
        >
            <option value="">{orgs.failed ? 'Couldn’t load organizations' : orgs.rows === null ? 'Loading…' : 'Pick an organization…'}</option>
            {/* keep a linked org selectable even before the list arrives */}
            {orgFilter && !orgRows.some((o) => String(o.id) === orgFilter) && <option value={orgFilter}>Org #{orgFilter}</option>}
            {orgRows.map((o) => (
                <option key={o.id} value={o.id}>
                    {o.name}
                </option>
            ))}
        </Select>
    );

    return (
        <AreaShell
            area="admin"
            tab="licenses"
            sub="License types and the licenses issued to organizations"
            actions={
                <>
                    <button className="tb-btn" onClick={() => openType(null)}>
                        Add type
                    </button>
                    <button className="tb-new" onClick={openIssue} disabled={types.rows === null || typeRows.length === 0}>
                        Issue license
                    </button>
                </>
            }
        >
            <div className="stack">
                <SectionCard title="Issued licenses" note={licenses ? String(licenses.length) : undefined}>
                    <div className="adm-pick">
                        {orgPicker}
                        {orgs.failed && (
                            <button type="button" className="tb-btn" onClick={orgs.reload}>
                                Retry
                            </button>
                        )}
                    </div>
                    {!orgFilter ? (
                        <p className="quiet-empty">Pick an organization to see its licenses.</p>
                    ) : licensesFailed ? (
                        <>
                            <p className="quiet-empty" style={{ marginBottom: 10 }}>Couldn’t load licenses.</p>
                            <button type="button" className="tb-btn" onClick={reloadLicenses}>
                                Retry
                            </button>
                        </>
                    ) : licenses === null ? (
                        <p className="quiet-empty">Loading…</p>
                    ) : licenses.length === 0 ? (
                        <p className="quiet-empty">No licenses for {orgName(orgFilter)} yet.</p>
                    ) : (
                        sortedLicenses.map((l) => {
                            const expired = isExpired(l);
                            return (
                                <div className="trow2 adm-row" key={l.id}>
                                    <span className="adm-main">
                                        <span className="nm2">{licenseLabel(l)}</span>
                                        <br />
                                        <span className="sub2 adm-mono" title={l.key}>{l.key}</span>
                                    </span>
                                    <span className="adm-acts">
                                        <span className={`sub2 ${expired ? 'adm-tone-bad' : 'adm-tone-good'}`}>
                                            {expired
                                                ? l.expiredAt
                                                    ? `Expired ${day(l.expiredAt)}`
                                                    : `Expired ${day(l.expiresAt as string)}`
                                                : l.expiresAt
                                                  ? `Until ${day(l.expiresAt)}`
                                                  : 'Active · no end date'}
                                        </span>
                                        {!expired && (
                                            <button
                                                type="button"
                                                className="tb-btn danger-soft"
                                                disabled={busyLicenseId !== null}
                                                aria-label={`Expire ${licenseLabel(l)} license ${keyTail(l.key)}`}
                                                onClick={() => setExpiring(l)}
                                            >
                                                {busyLicenseId === l.id ? 'Expiring…' : 'Expire…'}
                                            </button>
                                        )}
                                    </span>
                                </div>
                            );
                        })
                    )}
                    <p className="pg-sub adm-note">Expiring a license stops its devices right away.</p>
                </SectionCard>

                <SectionCard
                    title="License types"
                    note={types.rows ? String(typeRows.length) : undefined}
                    action={
                        <button type="button" className="tb-btn" onClick={() => openType(null)}>
                            Add type
                        </button>
                    }
                >
                    {types.failed ? (
                        <>
                            <p className="quiet-empty" style={{ marginBottom: 10 }}>Couldn’t load license types.</p>
                            <button type="button" className="tb-btn" onClick={types.reload}>
                                Retry
                            </button>
                        </>
                    ) : types.rows === null ? (
                        <p className="quiet-empty">Loading…</p>
                    ) : typeRows.length === 0 ? (
                        <p className="quiet-empty">No license types yet. Add one before issuing licenses.</p>
                    ) : (
                        typeRows.map((t) => (
                            <div className="trow2 adm-row" key={t.id}>
                                <span className="adm-main">
                                    <span className="nm2">{t.name}</span>
                                    <br />
                                    <span className="sub2">
                                        {[t.adminName, devices(t.simultaneousDeviceCount)].filter(Boolean).join(' · ')}
                                    </span>
                                    {t.description && (
                                        <>
                                            <br />
                                            <span className="sub2">{t.description}</span>
                                        </>
                                    )}
                                </span>
                                <span className="adm-acts">
                                    <button type="button" className="tb-btn" onClick={() => openFeatures(t)}>
                                        Features
                                    </button>
                                    <button type="button" className="tb-btn" onClick={() => openType(t)}>
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        className="tb-btn danger-soft"
                                        disabled={busyTypeId !== null}
                                        aria-label={`Delete ${t.name}`}
                                        onClick={() => setDeleting(t)}
                                    >
                                        {busyTypeId === t.id ? 'Deleting…' : 'Delete…'}
                                    </button>
                                </span>
                            </div>
                        ))
                    )}
                </SectionCard>
            </div>

            {/* create / edit type — hidden (not closed) while its confirm is up */}
            <Dialog
                open={typeDialog !== null && !typeConfirm}
                title={editingRow ? `Edit ${editingRow.name}` : 'Add license type'}
                onClose={() => !typeSaving && setTypeDialog(null)}
                onSubmit={submitType}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setTypeDialog(null)} disabled={typeSaving}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={typeSaving || (typeTouched && !typeReady)}>
                            {typeSaving ? 'Saving…' : editingRow ? 'Save' : 'Add type'}
                        </button>
                    </>
                }
            >
                <div className="two">
                    <Field label="Name" help="Shown to orgs." error={typeTouched && typeInvalid.name ? 'Required.' : undefined}>
                        <TextInput autoFocus value={typeDraft.name} onChange={(e) => setTypeDraft((d) => ({ ...d, name: e.target.value }))} />
                    </Field>
                    <Field label="Admin name" help="Shown to admins." error={typeTouched && typeInvalid.adminName ? 'Required.' : undefined}>
                        <TextInput value={typeDraft.adminName} onChange={(e) => setTypeDraft((d) => ({ ...d, adminName: e.target.value }))} />
                    </Field>
                </div>
                <Field label="Description (optional)">
                    <Textarea rows={3} value={typeDraft.description} onChange={(e) => setTypeDraft((d) => ({ ...d, description: e.target.value }))} />
                </Field>
                <Field
                    label="Simultaneous devices"
                    help={editingRow ? `Currently ${devices(editingRow.simultaneousDeviceCount)} per license.` : 'How many devices one license can run at once.'}
                    error={typeTouched && typeInvalid.count ? 'Enter a whole number, 1 or more.' : undefined}
                >
                    <TextInput
                        type="number"
                        inputMode="numeric"
                        min={1}
                        step={1}
                        value={typeDraft.simultaneousDeviceCount}
                        onChange={(e) => setTypeDraft((d) => ({ ...d, simultaneousDeviceCount: e.target.value }))}
                    />
                </Field>
                {typeError && (
                    <p className="field-e" role="alert">
                        {typeError}
                    </p>
                )}
            </Dialog>
            <ConfirmDialog
                open={typeConfirm}
                title={`Change ${editingRow?.name ?? ''}’s device limit?`}
                body={
                    editingRow
                        ? `Every organization holding a ${editingRow.name} license goes from ${devices(editingRow.simultaneousDeviceCount)} to ${devices(count)} at once, right away.${count < editingRow.simultaneousDeviceCount ? ' Orgs running more devices than that may see some stop authenticating.' : ''}`
                        : ''
                }
                actionLabel="Change limit"
                danger={!!editingRow && count < editingRow.simultaneousDeviceCount}
                onConfirm={saveType}
                onClose={() => setTypeConfirm(false)}
            />

            <ConfirmDialog
                open={deleting !== null}
                title={`Delete the ${deleting?.name ?? ''} license type?`}
                body={`No new ${deleting?.name ?? ''} licenses can be issued after this. If any organization still holds one, the delete may be refused. This can’t be undone.`}
                actionLabel="Delete type"
                onConfirm={() => deleting && removeType(deleting)}
                onClose={() => setDeleting(null)}
            />

            {/* feature grants */}
            <Dialog
                open={featureType !== null && !featuresConfirm}
                title={featureType ? `${featureType.name} features` : ''}
                onClose={closeFeatures}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={closeFeatures} disabled={featuresSaving}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="tb-new"
                            disabled={featuresSaving || granted === null || adds.length + removes.length === 0}
                            onClick={() => setFeaturesConfirm(true)}
                        >
                            {featuresSaving
                                ? 'Saving…'
                                : adds.length + removes.length > 0
                                  ? `Review ${adds.length + removes.length} change${adds.length + removes.length === 1 ? '' : 's'}`
                                  : 'No changes'}
                        </button>
                    </>
                }
            >
                <p className="pg-sub" style={{ margin: 0 }}>
                    Features every organization with a {featureType?.name} license gets. Nothing changes until you review and confirm.
                </p>
                {featuresFailed ? (
                    <div style={{ marginTop: 10 }}>
                        <p className="quiet-empty" style={{ marginBottom: 10 }}>Couldn’t load features.</p>
                        <button type="button" className="tb-btn" onClick={() => featureType && openFeatures(featureType)}>
                            Retry
                        </button>
                    </div>
                ) : granted === null || flags === null ? (
                    <p className="quiet-empty" style={{ marginTop: 10 }}>Loading…</p>
                ) : flags.length === 0 ? (
                    <p className="quiet-empty" style={{ marginTop: 10 }}>No features exist yet.</p>
                ) : (
                    <div className="adm-flags" role="group" aria-label="Granted features">
                        {flags.map((f) => (
                            <Checkbox
                                key={f.id}
                                label={f.key}
                                checked={grantDraft.has(f.id)}
                                disabled={featuresSaving}
                                onChange={(e) => {
                                    const on = e.target.checked;
                                    setGrantDraft((cur) => {
                                        const next = new Set(cur);
                                        if (on) next.add(f.id);
                                        else next.delete(f.id);
                                        return next;
                                    });
                                }}
                            />
                        ))}
                    </div>
                )}
            </Dialog>
            <ConfirmDialog
                open={featuresConfirm}
                title={`Change ${featureType?.name ?? ''}’s features?`}
                body={[
                    `Every organization holding a ${featureType?.name ?? ''} license`,
                    adds.length ? ` gains ${list(adds.map(flagKey))}` : '',
                    adds.length && removes.length ? ' and' : '',
                    removes.length ? ` loses ${list(removes.map(flagKey))}` : '',
                    ', right away.',
                ].join('')}
                actionLabel="Apply changes"
                danger={removes.length > 0}
                onConfirm={applyFeatures}
                onClose={() => setFeaturesConfirm(false)}
            />

            {/* issue */}
            <Dialog
                open={issueOpen && !issueConfirm}
                title="Issue a license"
                onClose={() => !issuing && setIssueOpen(false)}
                onSubmit={() => issueReady && !issuing && setIssueConfirm(true)}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setIssueOpen(false)} disabled={issuing}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={issuing || !issueReady}>
                            {issuing ? 'Issuing…' : 'Review'}
                        </button>
                    </>
                }
            >
                <Field label="Organization">
                    <Select
                        autoFocus
                        value={issueDraft.orgId}
                        onChange={(e) => setIssueDraft((d) => ({ ...d, orgId: e.target.value }))}
                    >
                        <option value="">Pick an organization…</option>
                        {orgRows.map((o) => (
                            <option key={o.id} value={o.id}>
                                {o.name}
                            </option>
                        ))}
                    </Select>
                </Field>
                <Field
                    label="License type"
                    help={issueType ? `${devices(issueType.simultaneousDeviceCount)} at once, plus the type’s features.` : undefined}
                >
                    <Select
                        value={issueDraft.licenseTypeId}
                        onChange={(e) => setIssueDraft((d) => ({ ...d, licenseTypeId: e.target.value }))}
                    >
                        <option value="">Pick a type…</option>
                        {typeRows.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.name}
                            </option>
                        ))}
                    </Select>
                </Field>
                <Field
                    label="Expires (optional)"
                    help="Leave empty for no end date."
                    error={pastExpiry ? 'Pick today or a later date.' : undefined}
                >
                    <TextInput
                        type="date"
                        min={today}
                        value={issueDraft.expiresAt}
                        onChange={(e) => setIssueDraft((d) => ({ ...d, expiresAt: e.target.value }))}
                    />
                </Field>
                {issueError && (
                    <p className="field-e" role="alert">
                        {issueError}
                    </p>
                )}
            </Dialog>
            <ConfirmDialog
                open={issueConfirm}
                title={`Issue a ${issueType?.name ?? ''} license to ${issueDraft.orgId ? orgName(issueDraft.orgId) : ''}?`}
                body={`${issueDraft.orgId ? orgName(issueDraft.orgId) : 'The org'} can run ${issueType ? devices(issueType.simultaneousDeviceCount) : 'its devices'} at once and gets the ${issueType?.name ?? ''} features, starting now${issueDraft.expiresAt ? ` until ${moment(issueDraft.expiresAt, 'YYYY-MM-DD').format('MMM D, YYYY')}` : ' with no end date'}.`}
                actionLabel="Issue license"
                danger={false}
                onConfirm={issue}
                onClose={() => setIssueConfirm(false)}
            />

            <ConfirmDialog
                open={expiring !== null}
                title={`Expire this ${expiring ? licenseLabel(expiring) : ''} license?`}
                body={`${orgFilter ? orgName(orgFilter) : 'The org'}’s license ${expiring ? keyTail(expiring.key) : ''} ends now. Devices using it stop authenticating right away. This can’t be undone — issue a new license to restore access.`}
                actionLabel="Expire license"
                onConfirm={() => expiring && expire(expiring)}
                onClose={() => setExpiring(null)}
            />
            {toast.node}
        </AreaShell>
    );
}
